if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.closeButton = this.querySelector('.announcement-bar__close');
      this.countdowns = this.querySelectorAll('[data-countdown]');
      this.sectionId = this.dataset.sectionId;
      this.storageKey = `announcement-bar-dismissed-${this.sectionId}`;

      this.handleClose = this.handleClose.bind(this);
      this.updateCountdowns = this.updateCountdowns.bind(this);
      this.publishHeight = this.publishHeight.bind(this);
      this.updateArrowPositions = this.updateArrowPositions.bind(this);
    }

    connectedCallback() {
      if (this.isDismissed()) {
        this.hide();
        return;
      }

      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this.closeButton?.addEventListener('click', this.handleClose);

      if (this.countdowns.length > 0) {
        this.initCountdowns();
        this.countdownInterval = setInterval(this.updateCountdowns, 1000);
      }

      this._initSlider(signal);

      this.publishHeight();
      this._resizeObserver = new ResizeObserver(this.publishHeight);
      this._resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.closeButton?.removeEventListener('click', this.handleClose);
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      this._stopAutoplay();
      this._resizeObserver?.disconnect();
      this._abortController?.abort();
    }

    publishHeight() {
      document.documentElement.style.setProperty('--announcement-bar-height', `${this.offsetHeight}px`);
    }

    _initSlider(signal) {
      this.slider = this.querySelector('.announcement-bar__slider');
      if (!this.slider) return;

      this.slides = [...this.slider.querySelectorAll('.slider__list > .announcement-bar__slide')];
      if (this.slides.length < 2) return;

      this.currentIndex = Math.max(0, this.slides.findIndex(slide => slide.classList.contains('is-active')));
      this.infinite = this.slider.dataset.infinite === 'true';
      this.autoplayEnabled = this.slider.dataset.autorotate === 'true';
      this.autoplaySpeed = (parseFloat(this.slider.dataset.autorotateSpeed) || 5) * 1000;
      this.prevBtn = this.slider.querySelector('.slider__button.prev');
      this.nextBtn = this.slider.querySelector('.slider__button.next');

      this._liveRegion = document.createElement('div');
      this._liveRegion.setAttribute('aria-live', 'polite');
      this._liveRegion.setAttribute('aria-atomic', 'true');
      this._liveRegion.classList.add('visually-hidden');
      this.slider.appendChild(this._liveRegion);

      this.prevBtn?.addEventListener('click', () => this._go(-1), { signal });
      this.nextBtn?.addEventListener('click', () => this._go(1), { signal });

      this.slider.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this._go(-1);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this._go(1);
        }
      }, { signal });

      this._setupGestures(signal);
      this._updateButtonStates();

      requestAnimationFrame(() => {
        this.updateArrowPositions();
        requestAnimationFrame(() => this.slider.classList.add('announcement-bar__slider--positioned'));
      });
      window.addEventListener('resize', () => this.updateArrowPositions(), { signal });

      if (this.autoplayEnabled) this._startAutoplay();
    }

    _setupGestures(signal) {
      const THRESHOLD = 40;
      let startX = null;
      let startY = null;
      let dragging = false;

      this.slider.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        if (e.target.closest('.slider__button, a')) return;
        startX = e.clientX;
        startY = e.clientY;
        dragging = true;
      }, { signal });

      this.slider.addEventListener('pointermove', (e) => {
        if (!dragging || startX === null) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
          e.preventDefault();
        }
      }, { signal, passive: false });

      const endDrag = (e) => {
        if (!dragging || startX === null) return;
        const dx = e.clientX - startX;
        dragging = false;
        startX = null;
        if (Math.abs(dx) >= THRESHOLD) this._go(dx < 0 ? 1 : -1);
      };
      this.slider.addEventListener('pointerup', endDrag, { signal });
      this.slider.addEventListener('pointercancel', () => {
        dragging = false;
        startX = null;
      }, { signal });

      let wheelCooldown = false;
      this.slider.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) || Math.abs(e.deltaX) < THRESHOLD) return;
        e.preventDefault();
        if (wheelCooldown) return;
        wheelCooldown = true;
        this._go(e.deltaX > 0 ? 1 : -1);
        setTimeout(() => { wheelCooldown = false; }, 400);
      }, { signal, passive: false });
    }

    _go(delta) {
      const total = this.slides.length;
      let target = this.currentIndex + delta;
      if (this.infinite) {
        target = ((target % total) + total) % total;
      } else {
        target = Math.max(0, Math.min(target, total - 1));
        if (target === this.currentIndex) return;
      }
      this._setActiveSlide(target, delta > 0 ? 1 : -1);
      if (this.autoplayEnabled) this._startAutoplay();
    }

    _setActiveSlide(targetIndex, direction) {
      if (targetIndex === this.currentIndex) return;
      const oldSlide = this.slides[this.currentIndex];
      const newSlide = this.slides[targetIndex];
      const enterClass = direction > 0 ? 'is-offset-right' : 'is-offset-left';
      const exitClass = direction > 0 ? 'is-offset-left' : 'is-offset-right';

      newSlide.style.transition = 'none';
      newSlide.classList.remove('is-active', 'is-offset-left', 'is-offset-right');
      newSlide.classList.add(enterClass);
      void newSlide.offsetWidth;
      newSlide.style.transition = '';

      oldSlide.classList.remove('is-active');
      oldSlide.classList.add(exitClass);
      oldSlide.setAttribute('aria-hidden', 'true');
      this._setFocusable(oldSlide, false);

      newSlide.classList.remove(enterClass);
      newSlide.classList.add('is-active');
      newSlide.setAttribute('aria-hidden', 'false');
      this._setFocusable(newSlide, true);

      this.currentIndex = targetIndex;
      this._announceSlide(targetIndex + 1, this.slides.length);
      this.updateArrowPositions();
      this._updateButtonStates();
    }

    _setFocusable(slide, focusable) {
      const focusables = slide.querySelectorAll('a,button,input,select,textarea,[tabindex]');
      focusables.forEach(el => {
        if (focusable) {
          const saved = el.getAttribute('data-tabindex-saved');
          if (saved !== null) {
            el.setAttribute('tabindex', saved);
            el.removeAttribute('data-tabindex-saved');
          } else {
            el.removeAttribute('tabindex');
          }
        } else {
          if (!el.hasAttribute('data-tabindex-saved')) {
            const current = el.getAttribute('tabindex');
            if (current !== null) el.setAttribute('data-tabindex-saved', current);
          }
          el.setAttribute('tabindex', '-1');
        }
      });
    }

    _announceSlide(current, total) {
      const label = theme.locales?.slider?.slideOf
        ? theme.locales.slider.slideOf.replace('[current]', current).replace('[total]', total)
        : current + ' / ' + total;
      this.slider.setAttribute('aria-busy', 'true');
      const span = document.createElement('span');
      span.textContent = label;
      this._liveRegion.innerHTML = '';
      this._liveRegion.appendChild(span);
      setTimeout(() => {
        this._liveRegion.innerHTML = '';
        this.slider.removeAttribute('aria-busy');
      }, 150);
    }

    _updateButtonStates() {
      if (!this.prevBtn && !this.nextBtn) return;
      if (this.infinite) {
        this.prevBtn?.removeAttribute('disabled');
        this.prevBtn?.removeAttribute('aria-disabled');
        this.nextBtn?.removeAttribute('disabled');
        this.nextBtn?.removeAttribute('aria-disabled');
        return;
      }
      const atStart = this.currentIndex === 0;
      const atEnd = this.currentIndex === this.slides.length - 1;
      if (this.prevBtn) {
        this.prevBtn.toggleAttribute('disabled', atStart);
        this.prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      }
      if (this.nextBtn) {
        this.nextBtn.toggleAttribute('disabled', atEnd);
        this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
      }
    }

    _startAutoplay() {
      this._stopAutoplay();
      this._autoplayTimer = setInterval(() => this._go(1), this.autoplaySpeed);
    }

    _stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    updateArrowPositions() {
      if (!this.slider) return;

      const activeSlide = this.slides[this.currentIndex];
      const message = activeSlide?.querySelector('.announcement-bar__message');
      if (!activeSlide || !message) return;

      // Computed purely from the declared (rem/px) tokens, never measured off
      // laid-out boxes: mixing independently-rounded getComputedStyle reads
      // can drift by a fraction of a px. The floor clamp (0px, i.e. flush
      // with the slider's own edge) is left to CSS's own max().
      const sliderStyle = getComputedStyle(this.slider);
      const buttonSizeRem = parseFloat(sliderStyle.getPropertyValue('--_arrow-button-size')) || 0;
      const gapPx = parseFloat(sliderStyle.getPropertyValue('--_arrow-gap')) || 0;
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const contentOffset = buttonSizeRem * rootFontSize + gapPx;

      const slideRect = activeSlide.getBoundingClientRect();
      const messageRect = message.getBoundingClientRect();

      const arrowLeftContent = messageRect.left - slideRect.left - contentOffset;
      const arrowRightContent = slideRect.right - messageRect.right - contentOffset;

      this.slider.style.setProperty('--_arrow-left-content', `${arrowLeftContent}px`);
      this.slider.style.setProperty('--_arrow-right-content', `${arrowRightContent}px`);
    }

    isDismissed() {
      try {
        return sessionStorage.getItem(this.storageKey) === 'true';
      } catch (e) {
        return false;
      }
    }

    handleClose() {
      try {
        sessionStorage.setItem(this.storageKey, 'true');
      } catch (e) {
        console.warn('Could not save dismissal state');
      }
      this.hide();
    }

    hide() {
      this.style.display = 'none';
      this.closest('.shopify-section')?.style.setProperty('display', 'none');
      document.documentElement.style.setProperty('--announcement-bar-height', '0px');
    }

    initCountdowns() {
      this.countdowns.forEach(countdown => {
        const endDateStr = countdown.dataset.endDate;
        if (!endDateStr) return;

        const endDate = new Date(endDateStr);
        if (isNaN(endDate.getTime())) {
          this.showExpiredMessage(countdown);
          return;
        }

        countdown.endDate = endDate;
        this.updateCountdown(countdown);
      });
    }

    updateCountdowns() {
      this.countdowns.forEach(countdown => {
        if (countdown.endDate) {
          this.updateCountdown(countdown);
        }
      });
    }

    updateCountdown(countdown) {
      const now = new Date();
      const timeLeft = countdown.endDate - now;

      if (timeLeft <= 0) {
        this.showExpiredMessage(countdown);
        return;
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

      const timerElement = countdown.querySelector('.announcement-bar__countdown-timer');
      if (!timerElement) return;

      let timeString = '';
      if (days > 0) {
        timeString = `${days}d ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      } else {
        timeString = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }

      timerElement.textContent = timeString;
    }

    showExpiredMessage(countdown) {
      const expiredMessage = countdown.dataset.expiredMessage;
      if (!expiredMessage) {
        countdown.style.display = 'none';
        return;
      }

      const contentElement = countdown.querySelector('.announcement-bar__countdown-content');
      if (contentElement) {
        const link = contentElement.closest('a');
        if (link) {
          link.textContent = expiredMessage;
        } else {
          contentElement.textContent = expiredMessage;
        }
      }
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
